import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../../common/LanguageSwitcher';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../../../hooks/useTheme';
import { authService } from '../../../services/auth/authService';
import { profileService } from '../../../services/profile/profileService';
import FeatherIcon from '../../common/FeatherIcon';
import { useDecryptionKey } from '../../../contexts/DecryptionKeyContext';

import {
  getEffectiveOrgModuleSlugs,
  isWelcomeCompleted,
  readNormalizedUserFromStorage,
} from '../../../utils/entitlements';
import AccountMenu from './AccountMenu';
import NotificationsPanel from './NotificationsPanel';
import HeaderBrand from './HeaderBrand';
import LensSwitcher from '../../lens/LensSwitcher';
import CommandBar from '../CommandBar/CommandBar';
import usePillarNav from '../Sidebar/pillarNav';
import { resolveIdentity } from './identity';
import { makeCanOpen } from './notificationSources';
import { DOCS_URL } from '../../../config/docs';

import './Header.css';

const HEADER_AGENT_STORAGE_KEY = 'saf3ai_header_agent_id';

const Header = ({ onMenuToggle, isMobile = false, showBrand = false }) => {
  const { t: translate } = useTranslation('layout');

  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  // The ⌘K bar is part of "New navigation" (account menu switch).
  const { on: pillarNav } = usePillarNav();

  // GET /auth/api/profile-details — null until it answers (or when it fails,
  // in which case the identity falls back to the localStorage user).
  const [profile, setProfile] = useState(null);

  // Re-read on every render so the badge dot vanishes the moment the user
  // dismisses the welcome page (which writes to localStorage). Cheap — just a
  // JSON.parse from storage and a string comparison.
  const welcomeUser = readNormalizedUserFromStorage();

  const welcomeIncomplete = !isWelcomeCompleted(welcomeUser.user_id ?? welcomeUser.id);

  // Who is signed in, for the account button: profile first, stored user second.
  const identity = resolveIdentity(profile, welcomeUser);

  // Module scope for the Notifications panel — the sidebar's entitlement rules,
  // so the panel never offers a module the navigation hides.
  const moduleSlugKey = getEffectiveOrgModuleSlugs().join(',');
  const roleName = welcomeUser.role_name || '';
  const { canOpenSection, canOpenModule } = useMemo(
    () => makeCanOpen({ moduleSlugs: moduleSlugKey ? moduleSlugKey.split(',') : [], roleName }),
    [moduleSlugKey, roleName],
  );

  const isMountedRef = useRef(true);
  const { isKeyLoaded, isKeyPersisted, clearKey } = useDecryptionKey();




  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    // Initialize feather icons with safety checks
    const feather = window.feather;
    if (!feather || !isMountedRef.current) return;

    const initIcons = () => {
      if (!isMountedRef.current) return;
      
      try {
        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
          if (!isMountedRef.current) return;
          
          try {
            // Only replace icons if component is still mounted
            const headerElement = document.querySelector('.top-header');
            if (headerElement && headerElement.isConnected) {
              feather.replace();
            }
          } catch (e) {
            console.debug('Feather icon initialization skipped:', e);
          }
        });
      } catch (e) {
        console.debug('Feather icon initialization error:', e);
      }
    };

    initIcons();
    
    // Re-initialize on mobile state change with delay
    const timer = setTimeout(() => {
      if (!isMountedRef.current) return;
      
      try {
        const headerElement = document.querySelector('.top-header');
        if (headerElement && headerElement.isConnected) {
          feather.replace();
        }
      } catch (e) {
        console.debug('Feather icon update skipped:', e);
      }
    }, 150);
    
    return () => {
      clearTimeout(timer);
    };
  }, [isMobile, theme]);

  // The header's agent / range filters and search are gone (Custom Agents pages
  // carry their own). An agent chosen there earlier is still in storage, and the
  // pages that read it would keep filtering by it with nothing on screen to say
  // so or to clear it: drop it.
  useEffect(() => {
    try {
      localStorage.removeItem(HEADER_AGENT_STORAGE_KEY);
      localStorage.removeItem('saf3ai_header_agent_name');
    } catch { /* storage blocked: nothing persisted to clear */ }
  }, []);

  // Profile for the account button (name, email, role, organisation).
  useEffect(() => {
    let cancelled = false;
    profileService.getProfile()
      .then((data) => { if (!cancelled && data && typeof data === 'object') setProfile(data); })
      .catch(() => { console.debug('Profile fetch unavailable, using localStorage fallback'); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    // Re-initialize feather icons on other updates
    if (!isMountedRef.current) return;
    
    const feather = window.feather;
    if (!feather) return;

    const timer = setTimeout(() => {
      if (!isMountedRef.current) return;
      
      try {
        const headerElement = document.querySelector('.top-header');
        if (headerElement && headerElement.isConnected) {
          feather.replace();
        }
      } catch (e) {
        console.debug('Feather icon replacement skipped:', e);
      }
    }, 100);
    
    return () => {
      clearTimeout(timer);
    };
  }, [isMobile]);


  const handleLogout = async () => {
    const returnPath = `${location.pathname}${location.search}${location.hash}`;
    try {
      await clearKey();
    } catch (e) {
      console.warn('clearKey on logout:', e);
    }
    try {
      await authService.logout({ returnPath });
    } catch (error) {
      console.error('Logout error:', error);
    }
    // Full navigation resets route state, axios in-flight errors, and lazy chunks reliably.
    window.location.replace('/');
  };

  return (
    <header className={`top-header ${isMobile ? 'mobile' : ''}`}>
      {isMobile && (
        <div 
          className={`header-menu-toggle mobile-visible`} 
          id="header-menu-toggle-btn" 
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
            if (onMenuToggle) {
              onMenuToggle(e);
            }
          }}
          onMouseDown={(e) => {
            // Prevent mousedown from triggering click outside handler
            e.stopPropagation();
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              if (onMenuToggle) {
                onMenuToggle(e);
              }
            }
          }}
          aria-label={translate('layout:headerHeader.toggleSidebarMenu')}
        >
          <FeatherIcon name="menu" size={22} />
        </div>
      )}

      {/* The brand, while the sidebar is the collapsed rail (see HeaderBrand). */}
      {showBrand && <HeaderBrand />}

      {/* Center spacer: keeps the controls on the right. The Custom Agents
          agent / range filters and global search that sat here were removed
          (2026-10-03); those pages carry their own. */}
      <div className="header-center-section" />

      {/* Right Section: Icons */}
      <div className="header-right-controls">
        {pillarNav && <CommandBar />}
        {/* Switch the persona Lens, left of the language picker. Pins are starred
            on the sidebar links. */}
        {/* Under the pillar layout the menu is personas only, and a persona trims the pillars
            to its pages (Sidebar.jsx, pillarNavConfig.js `trimPillars`). */}
        <LensSwitcher />
      {/* Renders only when the I18N flag is on for the org — see featureFlags.js */}
      <LanguageSwitcher className="me-2" />
      {isKeyLoaded && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginRight: '4px' }}>
          <button
            className="header-icon-wrapper"
            title={isKeyPersisted ? 'Decryption key loaded (saved to browser)' : 'Decryption key loaded (session only)'}
            style={{ color: '#22c55e', cursor: 'default' }}
          >
            <FeatherIcon name="key" size={18} />
          </button>
          <button
            className="header-icon-wrapper"
            title={translate('layout:headerHeader.clearDecryptionKey')}
            onClick={clearKey}
            style={{ padding: '2px', minWidth: 'auto' }}
          >
            <FeatherIcon name="x" size={14} />
          </button>
        </div>
      )}
        <button
          type="button"
          className="header-icon-wrapper"
          id="theme-toggle-btn"
          aria-label={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          onClick={toggleTheme}
        >
          <FeatherIcon 
            name={theme === 'dark' ? 'sun' : 'moon'} 
            size={18}
            className="theme-icon"
          />
        </button>

        <button
          className="header-icon-wrapper"
          id="welcome-toggle-btn"
          title={translate('layout:headerHeader.gettingStarted')}
          onClick={() => navigate('/welcome')}
          style={{ position: 'relative' }}
        >
          <FeatherIcon name="compass" size={18} />
          {welcomeIncomplete && (
            <span className="badge-dot" style={{ backgroundColor: '#0d6efd' }} />
          )}
        </button>

        {/* Product documentation lives on the external docs portal. */}
        <a
          className="header-icon-wrapper"
          id="docs-link-btn"
          href={DOCS_URL}
          target="_blank"
          rel="noopener noreferrer"
          title={translate('layout:headerHeader.documentation')}
          aria-label={translate('layout:headerHeader.documentation')}
        >
          <FeatherIcon name="book-open" size={18} />
        </a>

        {/* The Saf3AI Agent is launched from the floating draggable bubble
            (ChatBubble, rendered by AppLayout). The header zap icon opened the
            same panel and was redundant, so it was removed. The
            `saf3ai:open-agent` event it dispatched is still honoured by
            ChatBubble for any other caller. */}



        <NotificationsPanel
          email={identity.email}
          canOpenSection={canOpenSection}
          canOpenModule={canOpenModule}
        />

        <span className="hdr-divider" aria-hidden="true" />

        <AccountMenu identity={identity} onSignOut={handleLogout} />
      </div>
    </header>
  );
};

export default Header;
